import { SearchOutlined } from '@ant-design/icons';
import { Alert, Flex, Tree } from 'antd';
import Search from 'antd/lib/input/Search';
import { useMemo, useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import {
  MAX_PARAMETERS,
  MOBILE_MAX_PARAMETERS,
  PlotterMode,
} from 'business/data-analysis/constants';
import GraphSetSidebar, {
  GraphSetSidebarPlacement,
} from 'business/data-analysis/pages/graph/components/graph-set-side-bar';
import { ParameterCountTag } from 'business/data-analysis/pages/graph/components/parameter-count-tag';
import {
  useGraphSet,
  useGraphSetDispatch,
} from 'business/data-analysis/pages/graph/hooks/graph-context/contexts';
import { GraphSetDispatchActions } from 'business/data-analysis/pages/graph/hooks/graph-context/types';
import { useMode } from 'business/data-analysis/pages/graph/hooks/use-mode';
import { Module_Enum, useParameterFamiliesQuery } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import Button from 'ui/button';

import { useSearchParameters } from './hooks/searchParameters';
import { onSelectParameter } from './services/on-select-parameters';
import { SelectedNode, TreeNode } from './types';

export const ParameterSidebar = ({
  placement = 'right',
}: {
  placement?: GraphSetSidebarPlacement;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaType();
  const mode = useMode();
  const { currentConstructionSite } = useAppContext();
  const headers = useGraphqlApiHeaders(Module_Enum.DataAnalysis);

  invariant(currentConstructionSite, 'No construction site id');

  const {
    currentGraphId,
    structure: { graphs },
  } = useGraphSet();
  const currentGraph = graphs.get(currentGraphId ?? '');
  const dispatch = useGraphSetDispatch();

  const alreadySelectedParameters = useMemo(
    (): SelectedNode[] =>
      Array.from(graphs.get(currentGraphId ?? '')?.parameters ?? []).map(
        ([_, { id, name, unit }]) => {
          return { id, name, unit };
        },
      ),
    [currentGraphId, graphs],
  );

  const [selectedParameters, setSelectedParameters] = useState<SelectedNode[]>(
    mode === PlotterMode.TIME_PERIOD ? alreadySelectedParameters : [],
  );

  const { data: parameterFamilies } = useParameterFamiliesQuery({
    variables: { constructionSiteId: currentConstructionSite.id },
    context: {
      headers,
    },
    skip: !currentConstructionSite.id,
  });

  const {
    expandedKeys,
    setExpandedKeys,
    onExpand,
    baseTreeData,
    onChange,
    treeData,
    searchInputValue,
    setSearchInputValue,
  } = useSearchParameters(parameterFamilies?.parameter_familyDef ?? []);

  const onClose = () => {
    dispatch({
      type: GraphSetDispatchActions.SetCurrentGraphId,
      graphId: null,
    });
    setSelectedParameters([]);
    setExpandedKeys([]);
    setSearchInputValue('');
  };

  const onAddParametersToChart = (parametersToAdd: SelectedNode[]) => {
    if (!currentGraphId) {
      onClose();
      return;
    }

    dispatch({
      type: GraphSetDispatchActions.AddParameters,
      graphId: currentGraphId,
      parameters: parametersToAdd,
    });
    onClose();
  };

  const currentMaxParamters = isMobile ? MOBILE_MAX_PARAMETERS : MAX_PARAMETERS;

  const maxNumberOfParameters =
    mode === PlotterMode.RING
      ? currentMaxParamters - alreadySelectedParameters.length
      : currentMaxParamters;

  const totalNumberOfSelectedParameters =
    mode === PlotterMode.RING
      ? selectedParameters.length
      : alreadySelectedParameters.length + selectedParameters.length;

  const alreadySelectedMaxNumber =
    alreadySelectedParameters.length === currentMaxParamters;

  const selectedLimitCrossed =
    totalNumberOfSelectedParameters > maxNumberOfParameters;

  const displayTypeIsGraph = currentGraph?.displayType === 'graph';
  const disabledAdd =
    displayTypeIsGraph && (alreadySelectedMaxNumber || selectedLimitCrossed);

  return (
    <GraphSetSidebar
      title={
        <Flex vertical gap={8}>
          <Flex justify="center" gap={8}>
            <span>{t('dataAnalysis.labels.parameterList')}</span>
            <ParameterCountTag
              selectedParameterCount={totalNumberOfSelectedParameters}
            />
          </Flex>
        </Flex>
      }
      visible={currentGraphId !== null}
      onClose={onClose}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            onClick={() => onAddParametersToChart(selectedParameters)}
            type="primary"
            disabled={disabledAdd}
          >
            {t('dataAnalysis.actions.addParameters')}
          </Button>
        </Flex>
      }
      styles={{
        body: {
          display: 'flex',
          overflow: 'hidden',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          gap: 16,
        },
      }}
      placement={placement}
    >
      {disabledAdd ? (
        <Alert
          type="warning"
          closable={false}
          message={t('dataAnalysis.warning.maxNumberOfParameters', {
            context: alreadySelectedMaxNumber ? 'maxSelected' : 'limitCrossed',
            numberOfParameters: totalNumberOfSelectedParameters,
            maxParameterNumber: currentMaxParamters,
          })}
        />
      ) : null}

      <Search
        placeholder="Search"
        onChange={(event) => {
          onChange(event);
        }}
        value={searchInputValue}
      />

      {searchInputValue && treeData.length === 0 ? (
        <Flex vertical justify="center" align="center">
          <SearchOutlined />
          {t('dataAnalysis.parameterSideBar.noSearchResult')}
        </Flex>
      ) : null}

      <Tree
        checkable
        onCheck={(_, info) =>
          onSelectParameter(
            setSelectedParameters,
            // Cast to TreeNode because antd type does not include meta, children, etc. properties
            info as unknown as { node: TreeNode },
          )
        }
        checkedKeys={[
          ...(mode === PlotterMode.TIME_PERIOD
            ? alreadySelectedParameters
            : []),
          ...selectedParameters,
        ].map((param) => param.id)}
        treeData={searchInputValue ? treeData : baseTreeData}
        defaultExpandParent={true}
        selectable={false}
        onExpand={onExpand}
        expandedKeys={expandedKeys}
        rootStyle={{ overflow: 'auto' }}
      />
    </GraphSetSidebar>
  );
};
